import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Text,
  Alert
} from '@efast_public/fjd-component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { GovosApiRoutes, govosClient } from '../api/govos-api';
import { useNotification } from '../shared/notification/useNotification';

interface DeleteZustaendigeStelleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  zustaendigeStelle: {
    appiid: number;
    dispname: string;
  };
}

export function DeleteZustaendigeStelleModal({
  isOpen,
  onClose,
  onSuccess,
  zustaendigeStelle
}: DeleteZustaendigeStelleModalProps) {
  const { t } = useTranslation();
  const { notifyUserFromRequest } = useNotification();
  const queryClient = useQueryClient();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { mutate: deleteZustaendigeStelle } = useMutation({
    mutationFn: async () => {
      const result = await notifyUserFromRequest(
        () =>
          govosClient.delete(
            GovosApiRoutes.deleteZustaendigeStelle(zustaendigeStelle.appiid)
          ),
        {
          successMessage: t('api.success.deleteZustaendigeStelle'),
          errorMessage: t('api.error.deleteZustaendigeStelle'),
          loadingMessage: t('api.loading.deleteZustaendigeStelle')
        }
      );
      return result;
    },
    onSuccess: () => {
      queryClient
        .refetchQueries({
          queryKey: [GovosApiRoutes.startApps()]
        })
        .then(() => {
          onSuccess();
        });
    },
    onError: (error: unknown) => {
      const axiosError = error as AxiosError<{ error?: { msg?: string } }>;
      setDeleteError(
        axiosError?.response?.data?.error?.msg ??
          t('DeleteZustaendigeStelleModal.defaultErrorMessage')
      );
    }
  });

  const handleDelete = () => {
    setDeleteError(null);
    deleteZustaendigeStelle();
  };

  const handleClose = () => {
    setDeleteError(null);
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('DeleteZustaendigeStelleModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        {deleteError ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text strong>{t('DeleteZustaendigeStelleModal.errorTitle')}</Text>
            <Text>{deleteError}</Text>
          </div>
        ) : (
          <Alert
            variant="warning"
            message={t('DeleteZustaendigeStelleModal.confirmMessage', {
              name: zustaendigeStelle.dispname
            })}
          />
        )}
      </Modal.Content>
      <Modal.Footer>
        {deleteError ? (
          <Button
            variant="primary"
            onClick={handleClose}
            style={{ width: '100%' }}
          >
            {t('DeleteZustaendigeStelleModal.backButton')}
          </Button>
        ) : (
          <div
            style={{
              display: 'flex',
              gap: '12px',
              width: '100%'
            }}
          >
            <Button
              variant="secondary-outline"
              onClick={handleClose}
              style={{ flex: 1 }}
            >
              {t('DeleteZustaendigeStelleModal.cancelButton')}
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleDelete()}
              style={{ flex: 1 }}
            >
              {t('DeleteZustaendigeStelleModal.deleteButton')}
            </Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
}
